import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Images } from 'lucide-react';
import { EmptyState } from '@/components/shared/EmptyState';
import { QueryErrorState } from '@/components/shared/QueryErrorState';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useMediaLibraryQuery } from '../hooks/useMediaLibraryQuery';
import type { MediaPickerSelection } from '../service/types';
import { MEDIA_PAGE_SIZE } from '../utils/constants';
import { MediaGridSkeleton } from './MediaGridSkeleton';
import { MediaPagination } from './MediaPagination';
import { MediaThumbnail } from './MediaThumbnail';
import { MediaUploadButton } from './MediaUploadButton';

interface MediaPickerDialogProps {
  trigger: ReactNode;
  onSelect: (selection: MediaPickerSelection) => void;
}

/**
 * Pick one photo from the merchant's library. Hands back `{ id, url }` and
 * closes; the caller writes the URL into its own field. Uploading from here
 * is allowed so an empty library is not a dead end.
 */
export function MediaPickerDialog({ trigger, onSelect }: MediaPickerDialogProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('media.picker.title')}</DialogTitle>
          <DialogDescription>{t('media.picker.description')}</DialogDescription>
        </DialogHeader>

        {/* Mounted only while open, so the library is not fetched by every
            closed picker on the page (there is one per inventory row). */}
        <MediaPickerBody
          onSelect={(selection) => {
            onSelect(selection);
            setOpen(false);
          }}
        />

        <DialogFooter className="sm:justify-start">
          <MediaUploadButton variant="outline" size="sm" />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MediaPickerBody({ onSelect }: Pick<MediaPickerDialogProps, 'onSelect'>) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const libraryQuery = useMediaLibraryQuery({ page, page_size: MEDIA_PAGE_SIZE });
  const items = libraryQuery.data?.items ?? [];
  const totalPages = Math.max(1, Math.ceil((libraryQuery.data?.total ?? 0) / MEDIA_PAGE_SIZE));

  return (
    <div className="max-h-[60vh] space-y-4 overflow-y-auto">
      {libraryQuery.isLoading ? (
        <MediaGridSkeleton count={6} />
      ) : libraryQuery.isError ? (
        <QueryErrorState
          error={libraryQuery.error}
          isRetrying={libraryQuery.isRefetching}
          onRetry={libraryQuery.refetch}
        />
      ) : items.length === 0 ? (
        <EmptyState
          icon={<Images className="size-8" aria-hidden="true" />}
          title={t('media.picker.empty_title')}
          description={t('media.picker.empty_description')}
        />
      ) : (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {items.map((asset) => {
            const name = asset.filename?.trim() || t('media.asset.unnamed');
            return (
              <li key={asset.id}>
                <button
                  type="button"
                  className="focus-visible:ring-ring hover:bg-muted/60 flex w-full flex-col gap-1.5 rounded-lg border p-2 text-left transition-colors focus-visible:ring-2 focus-visible:outline-none"
                  aria-label={t('media.picker.choose_aria', { name })}
                  onClick={() => onSelect({ id: asset.id, url: asset.url })}
                >
                  <MediaThumbnail src={asset.url} alt="" />
                  <span className="truncate text-xs" title={name}>
                    {name}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <MediaPagination page={page} totalPages={totalPages} onPageChange={setPage} />
    </div>
  );
}
